import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import React, { useRef, useState } from 'react';
import { clamp, snap } from '@popmotion/popcorn';
import Box from '../Box';
import { useMeasure } from '../hooks';

function RangeSlider(props) {
  var _props$min = props.min,
      min = _props$min === void 0 ? 0 : _props$min,
      _props$max = props.max,
      max = _props$max === void 0 ? 100 : _props$max,
      _props$step = props.step,
      step = _props$step === void 0 ? 1 : _props$step,
      disabled = props.disabled,
      value = props.value,
      onChange = props.onChange,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? {} : _props$variant,
      restProps = _objectWithoutPropertiesLoose(props, ["min", "max", "step", "disabled", "value", "onChange", "variant"]);

  var snapTo = snap(step);
  var sliderRef = useRef();
  var trackRef = useRef();
  var thumbRef = useRef();
  var startPositionRef = useRef();
  var currentValueRef = useRef();

  var _useMeasure = useMeasure(),
      useMeasureRef = _useMeasure[0],
      useMeasureEffect = _useMeasure[1];

  var _useState = useState(0),
      sliderSize = _useState[0],
      setSliderSize = _useState[1];

  var _useState2 = useState(0),
      thumbSize = _useState2[0],
      setThumbSize = _useState2[1];

  var upperBound = sliderSize - thumbSize;

  function trimAlignValue(value) {
    return snapTo(clamp(min, max, value));
  }

  function getPointerPosition(event) {
    if (event.touches) {
      return event.touches[0].pageX;
    } else {
      return event.pageX;
    }
  }

  function getOffsetFromValue(value) {
    var range = max - min;
    var ratio = (value - min) / range;
    return ratio * upperBound;
  }

  function getValueFromOffset(offset) {
    var ratio = offset / upperBound;
    return ratio * (max - min) + min;
  }

  function getOffsetFromPosition(position) {
    var sliderRect = sliderRef.current.getBoundingClientRect();
    var sliderMin = sliderRect.left;
    var windowOffset = window.pageXOffset;
    var sliderStart = windowOffset + sliderMin;
    var pixelOffset = position - sliderStart - thumbSize / 2;
    return pixelOffset;
  }

  function getValueFromPosition(position) {
    var diffValue = position / (sliderSize - thumbSize) * (max - min);
    return trimAlignValue(currentValueRef.current + diffValue);
  }

  function handleThumbKeyDown(event) {
    if (event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
      case 'Left':
      case 'Down':
        event.preventDefault();
        onChange(trimAlignValue(value - step));
        break;

      case 'ArrowRight':
      case 'ArrowUp':
      case 'Right':
      case 'Up':
        event.preventDefault();
        onChange(trimAlignValue(value + step));
        break;

      case 'Home':
        event.preventDefault();
        onChange(min);
        break;

      case 'End':
        event.preventDefault();
        onChange(max);
        break;

      default:
    }
  }

  function handleTrackPointerDown(event) {
    var position = getPointerPosition(event);
    var valueAtPosition = trimAlignValue(getValueFromOffset(getOffsetFromPosition(position)));
    startPositionRef.current = position;
    currentValueRef.current = valueAtPosition;
    document.addEventListener('mousemove', handlePointerMove);
    document.addEventListener('mouseup', handlePointerUp);
    document.addEventListener('mouseleave', handlePointerUp);
    document.addEventListener('touchmove', handlePointerMove);
    document.addEventListener('touchend', handlePointerUp);
    document.addEventListener('touchcancel', handlePointerUp);
    event.preventDefault();
    thumbRef.current.focus();
    onChange(valueAtPosition);
  }

  function handlePointerMove(event) {
    var position = getPointerPosition(event);
    var positionDifference = position - startPositionRef.current;
    var nextValue = getValueFromPosition(positionDifference);
    event.preventDefault();
    onChange(nextValue);
  }

  function handlePointerUp() {
    document.removeEventListener('mousemove', handlePointerMove);
    document.removeEventListener('mouseup', handlePointerUp);
    document.removeEventListener('mouseleave', handlePointerUp);
    document.removeEventListener('touchmove', handlePointerMove);
    document.removeEventListener('touchend', handlePointerUp);
    document.removeEventListener('touchcancel', handlePointerUp);
  }

  useMeasureRef(sliderRef);
  useMeasureEffect(function () {
    var sliderRect = sliderRef.current.getBoundingClientRect();
    var sliderMin = sliderRect.left;
    var sliderMax = sliderRect.right;
    var thumbRect = thumbRef.current.getBoundingClientRect();
    var thumbSize = thumbRect.width;
    setSliderSize(Math.abs(sliderMax - sliderMin));
    setThumbSize(thumbSize);
  }, []);
  return /*#__PURE__*/React.createElement(Box, _extends({
    innerRef: sliderRef,
    width: "100%",
    paddingVertical: 0.5,
    position: "relative",
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.65 : undefined,
    onMouseDown: disabled ? undefined : handleTrackPointerDown,
    onTouchStart: disabled ? undefined : handleTrackPointerDown
  }, restProps), /*#__PURE__*/React.createElement(Box, _extends({
    innerRef: trackRef,
    height: 0.5,
    radius: 6,
    overflow: "hidden",
    backgroundColor: "grey-3",
    position: "relative"
  }, variant.track), /*#__PURE__*/React.createElement(Box, _extends({
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    transformOrigin: "center left",
    backgroundColor: "primary",
    style: {
      transform: "scaleX(" + (1 - (upperBound - getOffsetFromValue(value)) / (sliderSize - thumbSize)) + ")"
    }
  }, variant.progress))), /*#__PURE__*/React.createElement(Box, _extends({
    innerRef: thumbRef,
    tabIndex: 0,
    role: "slider",
    "aria-valuenow": value,
    "aria-valuemin": min,
    "aria-valuemax": max,
    "aria-disabled": disabled,
    onKeyDown: disabled ? undefined : handleThumbKeyDown,
    width: 1.5,
    height: 1.5,
    radius: "circle",
    position: "absolute",
    top: "50%",
    left: 0,
    backgroundColor: "grey-1",
    stroke: "grey-6",
    strokeWeight: 1,
    style: {
      transform: "translate(" + getOffsetFromValue(value) + "px, -50%)"
    }
  }, variant.thumb)));
}

export default RangeSlider;